import { useEffect, useRef, useState, useCallback } from 'react';
import type { Tenant, Product } from '../../lib/database.types';
import { loadMenu, type Menu, type MenuOption } from '../../lib/menu';
import { duplicateProduct, uniqueSlug, mergeOptionIds } from '../../lib/menuManager';
import { plannedOptionIdsForCategory, matchCategoryProfileKey } from '../../lib/categoryOptionProfiles';
import { moveInList, moveAcross } from '../../lib/menuOrder';
import {
  createProduct,
  updateProduct,
  updateProductEmoji,
  deleteProduct,
  reorderProducts,
  moveProductToCategory,
  createOption,
  reorderProductOptions,
  linkOptionToProduct,
  unlinkProductOption,
} from '../../lib/menuEditor';
import { formatPrice } from '../../lib/cart';
import { uploadProductImage } from '../../lib/storage';
import { supabase } from '../../lib/supabase';
import MenuImportPanel from './MenuImportPanel';
import ConfirmDelete from './ConfirmDelete';
import CategoriesManager from './CategoriesManager';
import OptionsLibrary from './OptionsLibrary';

/**
 * Éditeur de menu admin (US-504, façon Uber) — branché sur menuEditor (sauvegarde Supabase)
 * et menuOrder (ordre pur testé). Gère : carte (catégories/produits, glisser-déposer),
 * édition produit (nom/prix/photo), et édition des groupes d'options + valeurs (créer, éditer,
 * supprimer, réordonner par glisser-déposer, sous-options conditionnelles).
 */
export default function MenuManager({ tenant }: { tenant: Tenant }) {
  const [menu, setMenu] = useState<Menu | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Sous-onglet de la carte (parité legacy : Produits / Catégories / Bibliothèque options).
  const [menuTab, setMenuTab] = useState<'products' | 'categories' | 'options'>('products');
  // Filtre catégorie + recherche de l'onglet Produits (parité legacy).
  const [filterCat, setFilterCat] = useState<'all' | string>('all');
  const [search, setSearch] = useState('');
  // Verrou suppression par mot de passe (parité A2).
  const [pendingDelete, setPendingDelete] = useState<{ label: string; run: () => void } | null>(null);
  // Flash « ✓ Enregistré » du bouton Sauvegarder de la fiche produit.
  const [savedFlash, setSavedFlash] = useState(false);
  // Confirmation « Options par catégorie (auto) » (modèle Big Burger).
  const [pendingApplyProfiles, setPendingApplyProfiles] = useState(false);
  const [applyProfilesMsg, setApplyProfilesMsg] = useState<string | null>(null);
  // Bibliothèque d'options du tenant (pour activer une option existante sur un produit).
  const [libOptions, setLibOptions] = useState<{ id: string; name: string; selection: string; is_required: boolean; valuesCount: number }[]>([]);
  const dragProduct = useRef<{ id: string; categoryId: string } | null>(null);
  const dragGroup = useRef<string | null>(null);

  const reload = useCallback(async () => {
    setMenu(await loadMenu(tenant.id));
    // Charge la liste des options du tenant + nombre de valeurs (pour la modale produit).
    const { data: opts } = await supabase
      .from('options')
      .select('id, name, selection, is_required')
      .eq('tenant_id', tenant.id)
      .order('position');
    const list = (opts as { id: string; name: string; selection: string; is_required: boolean }[]) ?? [];
    const ids = list.map((o) => o.id);
    const counts: Record<string, number> = {};
    if (ids.length) {
      const { data: vals } = await supabase.from('option_values').select('option_id').in('option_id', ids);
      for (const v of (vals as { option_id: string }[]) ?? []) counts[v.option_id] = (counts[v.option_id] ?? 0) + 1;
    }
    setLibOptions(list.map((o) => ({ ...o, valuesCount: counts[o.id] ?? 0 })));
  }, [tenant.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true);
    try {
      await fn();
      await reload();
    } finally {
      setBusy(false);
    }
  };

  // Applique le modèle d'options par catégorie (Big Burger) à TOUS les produits :
  // pour chaque produit, options_json = profil de sa catégorie (slugs présents dans
  // la biblio du resto, dans l'ordre). Catégories sans profil (ex. Boissons) ignorées.
  const applyCategoryProfiles = () =>
    run(async () => {
      if (!menu) return;
      const { data: opts } = await supabase.from('options').select('id, slug').eq('tenant_id', tenant.id);
      const slugToId: Record<string, string> = {};
      for (const o of ((opts as { id: string; slug: string }[]) ?? [])) slugToId[o.slug] = o.id;
      let touched = 0;
      for (const c of menu.categories) {
        const ids = plannedOptionIdsForCategory(c.name, slugToId);
        if (ids.length === 0) continue;
        for (const p of menu.productsByCategory[c.id] ?? []) {
          await updateProduct(p.id, { options_json: ids });
          touched += 1;
        }
      }
      setApplyProfilesMsg(
        touched > 0
          ? `✅ Options appliquées à ${touched} produit(s) selon leur catégorie.`
          : 'ℹ️ Aucun produit concerné (aucune catégorie reconnue, ou biblio d\'options vide).',
      );
    })();

  if (!menu) return <p className="opacity-70">Chargement du menu…</p>;

  // Catégories du resto qui matchent un profil (pour l'aperçu du bouton auto).
  const matchedCategories = menu.categories.filter((c) => matchCategoryProfileKey(c.name));

  const allOptionSlugs = Object.values(menu.optionsByProduct)
    .flat()
    .map((o) => o.slug);
  const selected: Product | null = selectedId
    ? Object.values(menu.productsByCategory).flat().find((p) => p.id === selectedId) ?? null
    : null;
  const selectedOptions: MenuOption[] = selectedId ? menu.optionsByProduct[selectedId] ?? [] : [];

  // ----- produits -----
  const addProduct = (categoryId: string, slugs: string[]) =>
    run(async () => {
      const created = await createProduct(tenant.id, categoryId, {
        name: 'Nouveau produit',
        slug: uniqueSlug('Nouveau produit', slugs),
        base_price_cents: 0,
      });
      if (created) setSelectedId(created.id);
    })();

  const duplicate = (p: Product, slugs: string[]) =>
    run(async () => {
      const c = duplicateProduct(p, slugs);
      await createProduct(tenant.id, p.category_id, {
        name: c.name,
        slug: c.slug,
        base_price_cents: c.base_price_cents,
        description: c.description,
        image_url: c.image_url,
        position: c.position,
      });
    })();

  const onProductDrop = (categoryId: string, beforeId: string | null) => {
    const drag = dragProduct.current;
    dragProduct.current = null;
    if (!drag || drag.id === beforeId) return;

    // Même catégorie → simple réordonnancement.
    if (drag.categoryId === categoryId) {
      const ids = (menu.productsByCategory[categoryId] ?? []).map((p) => p.id);
      if (!ids.includes(drag.id)) return;
      const ordered = moveInList(ids, drag.id, beforeId);
      run(() => reorderProducts(ordered))();
      return;
    }

    // Catégorie différente → déplacement inter-catégories + réécriture des positions cibles.
    const fromIds = (menu.productsByCategory[drag.categoryId] ?? []).map((p) => p.id);
    const toIds = (menu.productsByCategory[categoryId] ?? []).map((p) => p.id);
    if (!fromIds.includes(drag.id)) return;
    const { to } = moveAcross(fromIds, toIds, drag.id, beforeId);
    run(() => moveProductToCategory(drag.id, categoryId, to))();
  };

  // Déplace le produit glissé vers une CATÉGORIE cible (dépôt sur une puce catégorie),
  // en l'ajoutant à la fin de la catégorie. Marche depuis n'importe quelle vue.
  const dropProductOnCategory = (categoryId: string) => {
    const drag = dragProduct.current;
    dragProduct.current = null;
    if (!drag || drag.categoryId === categoryId) return;
    const toIds = (menu.productsByCategory[categoryId] ?? []).map((p) => p.id);
    run(() => moveProductToCategory(drag.id, categoryId, [...toIds, drag.id]))();
  };

  // ----- groupes d'options -----
  // Le produit pilote-t-il ses options via `options_json` (choix explicite admin) ?
  // Si oui, attache/détache/réordre doivent réécrire `options_json` (sinon aucun effet visible).
  const usesJson = Array.isArray(selected?.options_json) && (selected!.options_json as string[]).length > 0;
  const currentOptionIds = selectedOptions.map((o) => o.id!).filter(Boolean);

  const addGroup = () =>
    run(async () => {
      if (!selectedId) return;
      const opt = await createOption(tenant.id, {
        name: 'Nouvelle option',
        slug: uniqueSlug('Nouvelle option', allOptionSlugs),
      });
      if (!opt) return;
      if (usesJson) await updateProduct(selectedId, { options_json: [...currentOptionIds, opt.id] });
      else await linkOptionToProduct(selectedId, opt.id, selectedOptions.length);
    })();

  const attachOption = (optId: string) =>
    run(async () => {
      if (!selectedId) return;
      if (usesJson) await updateProduct(selectedId, { options_json: [...currentOptionIds, optId] });
      else await linkOptionToProduct(selectedId, optId, selectedOptions.length);
    })();

  const detachOption = (o: MenuOption) =>
    run(async () => {
      if (!selectedId) return;
      if (usesJson) await updateProduct(selectedId, { options_json: currentOptionIds.filter((id) => id !== o.id) });
      else if (o.linkId) await unlinkProductOption(o.linkId);
    })();

  // Copie les options d'un autre produit (comme Uber) : ajoute les groupes manquants
  // à ce produit sans toucher aux siens. Écrit options_json (choix admin explicite).
  const copyOptionsFrom = (sourceProductId: string) =>
    run(async () => {
      if (!selectedId || !sourceProductId) return;
      const sourceIds = (menu.optionsByProduct[sourceProductId] ?? []).map((o) => o.id!).filter(Boolean);
      if (!sourceIds.length) return;
      await updateProduct(selectedId, { options_json: mergeOptionIds(currentOptionIds, sourceIds) });
    })();

  // Sauvegarde explicite : tout est déjà auto-enregistré (onBlur) ; on force le flush
  // du champ actif, on affiche « ✓ Enregistré », puis on ferme la fiche.
  const saveAndClose = () => {
    (document.activeElement as HTMLElement | null)?.blur?.();
    setSavedFlash(true);
    window.setTimeout(() => {
      setSavedFlash(false);
      setSelectedId(null);
    }, 600);
  };

  const onGroupDrop = (beforeOptionId: string | null) => {
    const dragged = dragGroup.current;
    dragGroup.current = null;
    if (!dragged) return;
    if (usesJson) {
      const ordered = moveInList(currentOptionIds, dragged, beforeOptionId);
      run(() => updateProduct(selectedId!, { options_json: ordered }))();
      return;
    }
    const linkOf = (oid: string | null) => (oid ? selectedOptions.find((o) => o.id === oid)?.linkId ?? null : null);
    const linkIds = selectedOptions.map((o) => o.linkId!).filter(Boolean);
    const draggedLink = linkOf(dragged);
    if (!draggedLink || !linkIds.includes(draggedLink)) return;
    const ordered = moveInList(linkIds, draggedLink, linkOf(beforeOptionId));
    run(() => reorderProductOptions(ordered))();
  };

  const onPhotoFile = (productId: string, file: File | undefined) => {
    if (!file) return;
    run(async () => {
      const url = await uploadProductImage(tenant.id, file);
      if (url) await updateProduct(productId, { image_url: url });
    })();
  };


  const TABS: { id: 'products' | 'categories' | 'options'; label: string }[] = [
    { id: 'products', label: '🍔 Produits' },
    { id: 'categories', label: '📂 Catégories' },
    { id: 'options', label: '⚙️ Bibliothèque options' },
  ];

  return (
    <div className="w-full max-w-5xl">
      <div className="mb-5 flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setMenuTab(t.id);
              if (t.id === 'products') reload();
            }}
            className={
              'border-b-2 px-4 py-2.5 text-sm font-semibold -mb-px ' +
              (menuTab === t.id ? '' : 'border-transparent text-slate-500 hover:text-slate-700')
            }
            style={menuTab === t.id ? { color: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {menuTab === 'categories' && <CategoriesManager tenant={tenant} />}
      {menuTab === 'options' && <OptionsLibrary tenant={tenant} />}

      {menuTab === 'products' && (() => {
        const allProducts = menu.categories.flatMap((c) =>
          (menu.productsByCategory[c.id] ?? []).map((p) => ({ p, cat: c })),
        );
        const allSlugs = allProducts.map((x) => x.p.slug);
        const q = search.trim().toLowerCase();
        const visible = allProducts.filter(({ p, cat }) => {
          if (filterCat !== 'all' && cat.id !== filterCat) return false;
          if (q && !(`${p.name} ${p.description ?? ''}`.toLowerCase().includes(q))) return false;
          return true;
        });
        const dragEnabled = filterCat !== 'all' && q === '';
        const newProductCat = filterCat !== 'all' ? filterCat : menu.categories[0]?.id;
        const optCount = (id: string) => (menu.optionsByProduct[id] ?? []).length;

        return (
        <div>
          <MenuImportPanel tenantId={tenant.id} currency={tenant.currency} onImported={reload} />

          {/* Barre d'outils : filtre catégorie + recherche + nouveau produit */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <select
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="all">Toutes catégories ({allProducts.length})</option>
              {menu.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.emoji} {c.name} ({(menu.productsByCategory[c.id] ?? []).length})
                </option>
              ))}
            </select>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit (nom, description, emoji)…"
              className="min-w-[12rem] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <button
              disabled={busy || !newProductCat}
              onClick={() => newProductCat && addProduct(newProductCat, allSlugs)}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
              style={{ background: 'var(--brand-primary)' }}
            >
              + Nouveau produit
            </button>
            <button
              disabled={busy || matchedCategories.length === 0}
              onClick={() => { setApplyProfilesMsg(null); setPendingApplyProfiles(true); }}
              className="rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 disabled:opacity-40"
              title="Applique les options standard par catégorie (modèle Big Burger) à tous les produits"
            >
              🧩 Options par catégorie (auto)
            </button>
          </div>
          {applyProfilesMsg && (
            <p className="mb-3 rounded-lg bg-slate-50 p-3 text-sm">{applyProfilesMsg}</p>
          )}
          {dragEnabled ? (
            <p className="mb-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
              ⠿ Glisser-déposer : réordonnez les produits, ou déposez un produit sur une catégorie ci-dessous pour l'y déplacer.
            </p>
          ) : (
            <p className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              💡 Astuce : glissez un produit sur une catégorie ci-dessous pour l'y déplacer. Choisissez une catégorie pour réordonner à l'intérieur.
            </p>
          )}

          {/* Catégories = cibles de dépôt : glisser un produit dessus le déplace ; cliquer filtre. */}
          <div className="mb-4 flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs font-semibold text-slate-500">Déplacer vers :</span>
            {menu.categories.map((c) => (
              <button
                key={c.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => dropProductOnCategory(c.id)}
                onClick={() => setFilterCat(c.id)}
                className={
                  'rounded-full border px-2.5 py-1 text-xs font-semibold transition ' +
                  (filterCat === c.id ? 'border-brand-primary bg-brand-light text-brand-primary' : 'border-slate-200 bg-slate-50 hover:border-brand-primary')
                }
                title={`Déposer un produit ici pour le mettre dans « ${c.name} », ou cliquer pour filtrer`}
              >
                {c.emoji} {c.name}
              </button>
            ))}
          </div>

          {/* Grille de cartes produit */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visible.map(({ p, cat }) => (
              <div
                key={p.id}
                draggable
                onDragStart={() => (dragProduct.current = { id: p.id, categoryId: cat.id })}
                onDragOver={(e) => dragEnabled && e.preventDefault()}
                onDrop={() => dragEnabled && onProductDrop(cat.id, p.id)}
                className="flex cursor-grab flex-col overflow-hidden rounded-xl border border-slate-200 bg-white"
              >
                <div className="relative flex h-32 items-center justify-center bg-slate-100">
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-4xl">{p.emoji || '🍽️'}</span>
                  )}
                  {dragEnabled && <span className="absolute right-2 top-2 cursor-grab rounded bg-white/90 px-1.5 py-0.5 text-sm text-slate-500 shadow select-none" title="Glisser pour réordonner">⠿</span>}
                  {p.is_featured && <span className="absolute left-2 top-2 rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">★ POPULAIRE</span>}
                  {!p.is_available && <span className={'absolute top-2 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white ' + (dragEnabled ? 'right-9' : 'right-2')}>RUPTURE</span>}
                  {!p.image_url && <span className="absolute bottom-2 left-2 rounded bg-black/40 px-1.5 py-0.5 text-[10px] font-semibold text-white">📸 Sans photo</span>}
                </div>
                <div className="flex flex-1 flex-col p-3">
                  <div className="text-sm font-bold leading-tight">{p.name}</div>
                  <div className="mb-1 line-clamp-2 min-h-[32px] text-xs text-slate-500">{p.description}</div>
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="text-base font-bold" style={{ color: 'var(--brand-primary)' }}>{formatPrice(p.base_price_cents, tenant.currency)}</span>
                    <span className="text-[11px] text-slate-400">{optCount(p.id)} option{optCount(p.id) > 1 ? 's' : ''}</span>
                  </div>
                  <div className="mt-auto grid grid-cols-2 gap-1.5">
                    <button onClick={run(() => updateProduct(p.id, { is_available: !p.is_available }))} className={'col-span-2 rounded-md px-2 py-1 text-xs font-semibold ' + (p.is_available ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
                      {p.is_available ? '✓ En stock' : '✕ Rupture'}
                    </button>
                    <button onClick={() => setSelectedId(p.id)} className="rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white">Éditer</button>
                    <button onClick={() => duplicate(p, allSlugs)} className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">⎘ Dupliquer</button>
                    <button onClick={() => setPendingDelete({ label: `le produit « ${p.name} »`, run: run(() => { setSelectedId(null); return deleteProduct(p.id); }) })} className="col-span-2 rounded-md px-2 py-1 text-xs text-slate-400 hover:bg-red-50 hover:text-red-600">🗑️ Supprimer</button>
                  </div>
                </div>
              </div>
            ))}
            {visible.length === 0 && (
              <p className="col-span-full py-8 text-center text-sm text-slate-400">
                Aucun produit {allProducts.length > 0 ? 'pour ce filtre' : '— ajoutez-en un'}.
              </p>
            )}
          </div>
        </div>
        );
      })()}

      {/* ---- Modale d'édition du produit sélectionné ---- */}
      {menuTab === 'products' && selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <div className="my-6 w-full max-w-3xl rounded-2xl bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-bold">Modifier le produit</h3>
              <button onClick={() => setSelectedId(null)} className="text-slate-400 hover:text-slate-700" title="Fermer">✕</button>
            </div>
          <div>
            <label className="block text-xs font-semibold opacity-60 mb-1">Nom</label>
            <input key={selected.id + 'n'} defaultValue={selected.name}
              onBlur={(e) => e.target.value !== selected.name && run(() => updateProduct(selected.id, { name: e.target.value }))()}
              className="w-full border border-slate-200 rounded-lg p-2 mb-3" />

            <label className="block text-xs font-semibold opacity-60 mb-1">Catégorie</label>
            <select key={selected.id + 'c'} value={selected.category_id}
              onChange={(e) => e.target.value !== selected.category_id && run(() => updateProduct(selected.id, { category_id: e.target.value }))()}
              className="w-full border border-slate-200 rounded-lg p-2 mb-3">
              {menu.categories.map((c) => (
                <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>
              ))}
            </select>

            <label className="block text-xs font-semibold opacity-60 mb-1">Emoji (repli si pas de photo)</label>
            <input key={selected.id + 'e'} defaultValue={selected.emoji ?? ''} maxLength={4} placeholder="🍔"
              onBlur={(e) => e.target.value !== (selected.emoji ?? '') && run(() => updateProductEmoji(selected.id, e.target.value || null))()}
              className="w-24 border border-slate-200 rounded-lg p-2 mb-3 text-center text-lg" />

            <label className="block text-xs font-semibold opacity-60 mb-1">Description</label>
            <textarea key={selected.id + 'd'} defaultValue={selected.description ?? ''} rows={2}
              placeholder="Ex. : pain brioché, steak smashé, cheddar, sauce maison…"
              onBlur={(e) => e.target.value !== (selected.description ?? '') && run(() => updateProduct(selected.id, { description: e.target.value || null }))()}
              className="w-full border border-slate-200 rounded-lg p-2 mb-3 text-sm" />

            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className="block text-xs font-semibold opacity-60 mb-1">Prix (€)</label>
                <input key={selected.id + 'p'} type="number" step="0.10" defaultValue={(selected.base_price_cents / 100).toFixed(2)}
                  onBlur={(e) => { const c = Math.round(parseFloat(e.target.value || '0') * 100); if (c !== selected.base_price_cents) run(() => updateProduct(selected.id, { base_price_cents: c }))(); }}
                  className="w-full border border-slate-200 rounded-lg p-2" />
              </div>
              <div className="flex-[2]">
                <label className="block text-xs font-semibold opacity-60 mb-1">Photo</label>
                <div className="flex items-center gap-2">
                  {selected.image_url && <img src={selected.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />}
                  <label className="cursor-pointer text-sm text-brand-primary border border-dashed border-slate-300 rounded-lg px-2 py-1.5 hover:bg-brand-light whitespace-nowrap">
                    📷 Importer
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => onPhotoFile(selected.id, e.target.files?.[0])} />
                  </label>
                </div>
                <input key={selected.id + 'i'} defaultValue={selected.image_url ?? ''} placeholder="ou coller une URL https://…"
                  onBlur={(e) => e.target.value !== (selected.image_url ?? '') && run(() => updateProduct(selected.id, { image_url: e.target.value || null }))()}
                  className="w-full border border-slate-200 rounded-lg p-2 mt-1 text-sm" />
              </div>
            </div>

            <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 mb-3 text-sm">
              <input
                type="checkbox"
                defaultChecked={selected.is_featured}
                onChange={(e) => run(() => updateProduct(selected.id, { is_featured: e.target.checked }))()}
              />
              <span className="font-semibold">★ Best-seller</span>
              <span className="opacity-50">(mis en avant sur la page d'accueil)</span>
            </label>

            <div className="flex items-center justify-between mt-4 mb-1">
              <h3 className="text-sm font-bold">Options de personnalisation</h3>
              <button disabled={busy} onClick={addGroup} className="text-sm text-brand-primary">+ Créer une option</button>
            </div>
            <p className="mb-2 text-xs text-slate-400">
              Cochez les options à proposer sur ce produit. Pour modifier les valeurs, prix ou type d'une option, allez dans l'onglet <strong>Bibliothèque options</strong>.
            </p>
            <p className="mb-2 text-xs font-semibold text-emerald-700">✓ Activées · {selectedOptions.length} option{selectedOptions.length > 1 ? 's' : ''} — glissez ⠿ pour réordonner</p>

            {/* Copier les options d'un autre produit (comme Uber) pour aller plus vite */}
            {(() => {
              const sources = Object.values(menu.productsByCategory)
                .flat()
                .filter((p) => p.id !== selected.id && (menu.optionsByProduct[p.id]?.length ?? 0) > 0);
              if (sources.length === 0) return null;
              return (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-blue-50 px-3 py-2">
                  <label className="text-xs font-semibold text-blue-800">⎘ Copier les options d'un autre produit</label>
                  <select
                    disabled={busy}
                    value=""
                    onChange={(e) => { if (e.target.value) copyOptionsFrom(e.target.value); }}
                    className="min-w-[10rem] flex-1 rounded-lg border border-blue-200 bg-white p-1.5 text-sm"
                  >
                    <option value="">— choisir un produit —</option>
                    {sources.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} ({menu.optionsByProduct[p.id]?.length ?? 0} option{(menu.optionsByProduct[p.id]?.length ?? 0) > 1 ? 's' : ''})</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            <ul onDragOver={(e) => e.preventDefault()}>
              {selectedOptions.map((o) => (
                <li key={o.id ?? o.slug} draggable
                  onDragStart={() => (dragGroup.current = o.id ?? null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onGroupDrop(o.id ?? null)}
                  className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2 mb-1.5">
                  <span className="cursor-grab text-slate-400 select-none" title="Glisser pour réordonner">⠿</span>
                  <input type="checkbox" checked onChange={() => detachOption(o)} className="cursor-pointer" title="Désactiver cette option" />
                  <span className="flex-1 font-semibold text-sm">{o.name}</span>
                  <span className="text-xs text-slate-500">{o.values.length} valeur{o.values.length > 1 ? 's' : ''} · {o.required ? 'Obligatoire' : 'Facultatif'} · {o.type === 'multi' ? 'Multi' : 'Unique'}</span>
                </li>
              ))}
            </ul>
            {selectedOptions.length === 0 && <p className="text-sm opacity-60">Aucune option activée. Cochez-en une ci-dessous ou créez-en une nouvelle.</p>}

            {/* Options disponibles dans la bibliothèque (à activer sur ce produit) */}
            {(() => {
              const attachedIds = new Set(selectedOptions.map((o) => o.id).filter(Boolean) as string[]);
              const available = libOptions.filter((o) => !attachedIds.has(o.id));
              if (available.length === 0) return null;
              return (
                <div className="mt-4">
                  <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Disponibles ({available.length}) — cliquez pour activer</p>
                  <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {available.map((o) => (
                      <li key={o.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                        <input type="checkbox" checked={false} disabled={busy || !selectedId} onChange={() => attachOption(o.id)} className="cursor-pointer" title="Activer cette option" />
                        <span className="flex-1 font-semibold">{o.name}</span>
                        <span className="text-xs text-slate-500">{o.valuesCount} valeur{o.valuesCount > 1 ? 's' : ''} · {o.is_required ? 'Obligatoire' : 'Facultatif'} · {o.selection === 'multiple' ? 'Multi' : 'Unique'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>

          {/* Pied : sauvegarde explicite (tout est déjà auto-enregistré) */}
          <div className="mt-5 flex items-center justify-between border-t pt-4">
            <span className="text-xs text-slate-400">💾 Vos modifications sont enregistrées automatiquement.</span>
            <div className="flex items-center gap-2">
              {savedFlash && <span className="text-xs font-semibold text-emerald-600">✓ Enregistré</span>}
              <button onClick={() => setSelectedId(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold">Fermer</button>
              <button onClick={saveAndClose} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" style={{ background: 'var(--brand-primary)' }}>
                💾 Enregistrer
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      <ConfirmDelete
        open={!!pendingDelete}
        label={pendingDelete?.label ?? ''}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          pendingDelete?.run();
          setPendingDelete(null);
        }}
      />

      {/* Confirmation : appliquer le modèle d'options par catégorie */}
      {pendingApplyProfiles && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5">
            <h3 className="mb-2 text-lg font-bold">🧩 Options par catégorie (modèle Big Burger)</h3>
            <p className="mb-3 text-sm text-slate-600">
              Chaque produit recevra le jeu d'options standard de sa catégorie, dans le bon ordre.
              Les catégories reconnues seront traitées ; les autres (ex. Boissons) sont laissées telles quelles.
            </p>
            <p className="mb-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
              ⚠️ Ceci <strong>remplace</strong> les options actuellement affectées aux produits concernés.
              Idéal sur un nouveau site ; à utiliser avec précaution sur un menu déjà personnalisé.
            </p>
            <p className="mb-4 text-xs text-slate-500">
              Catégories reconnues : {matchedCategories.map((c) => c.name).join(', ') || '—'}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPendingApplyProfiles(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold">Annuler</button>
              <button
                disabled={busy}
                onClick={() => { setPendingApplyProfiles(false); applyCategoryProfiles(); }}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
                style={{ background: 'var(--brand-primary)' }}
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
